import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Dialog, Transition } from "@headlessui/react";
import CLOSE from "../../../../assets/images/icons/close.svg";
import Input from "../../../inputs/inputs";
import Button from "../../../buttons/buttons";
import { Beat } from "../../../../plugins/spinners.plugin";
import { searchDeliveryModes } from "../../../../redux/deliveryMode/deliveryModeAction";
import deliveryModeType from "../../../../redux/deliveryMode/deliveryModeTypes";
import {
  attachShipperDeliveryMode,
  showShipper,
} from "../../../../redux/shippers/shippersActions";
import { getAllCurrencies } from "../../../../redux/currency/currencyAction";

const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

export default React.forwardRef(function AttachDeliveryMode(
  {
    visibility = false,
    disableClose = false,
    disableOpen = false,
    onOpen = () => { },
    onClose = () => { },
    shipper_id,
  },
  ref
) {
  React.useImperativeHandle(ref, () => ({
    open: () => openModal(),
    close: () => closeModal(),
  }));

  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = React.useState(visibility);
  const [submitted, setSubmitted] = useState(false);
  const [serviceArea, setServiceArea] = React.useState("");
  const [input, setInput] = useState({
    delivery_mode_id: "",
    charge: "",
    category: "",
    currency: "",
    operating_status: "",
  });

  const [operating_time, set_operating_time] = useState(() =>
    days.reduce((acc, day) => ({
      ...acc,
      [`operating_time_${day}_open`]: "00:00",
      [`operating_time_${day}_close`]: "00:00",
    }), {})
  );

  const { search } = useSelector((state) => state.deliveryMode);
  const { allTimezones, isLoading } = useSelector((state) => state.timezone);
  const { allCurrencies } = useSelector((state) => state.currency);
  const { updating } = useSelector((state) => state.shipper);

  const [time_zone, setTimeZone] = useState("");

  function closeModal() {
    !disableClose && setIsOpen(false);
    !disableClose && onClose();
  }

  function openModal() {
    !disableOpen && setIsOpen(true);
    !disableOpen && onOpen();
  }

  let currencies = [];
  for (const alphabetic_code in allCurrencies) {
    currencies.push(allCurrencies[alphabetic_code]);
  }

  useEffect(() => {
    dispatch(getAllCurrencies());
    // eslint-disable-next-line
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    e.preventDefault();
    setInput({ ...input, [name]: value });
  };

  const handleSearch = (event) => {
    event.preventDefault();
    const { value } = event.target;
    setServiceArea(value);
    if (value.length >= 3) {
      dispatch(searchDeliveryModes({ search: value }));
    } else {
      dispatch({ type: deliveryModeType["SEARCH_DELIVERY_MODE"], payLoad: [] });
    }
  };

  function selectDeliveryMode(result) {
    setInput((prev) => ({ ...prev, delivery_mode_id: result.id ?? "" }));
    setServiceArea(result.title ?? "");
    dispatch({ type: deliveryModeType["SEARCH_DELIVERY_MODE"], payLoad: [] });
  }

  const handleOperatingTimeChange = (day, type, value) => {
    set_operating_time((prev) => ({
      ...prev,
      [`operating_time_${day}_${type}`]: value,
    }));
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    if (
      !input.delivery_mode_id ||
      !input.charge ||
      !input.category ||
      !input.currency ||
      !time_zone ||
      !input.operating_status
    ) {
      return;
    }
    await dispatch(
      attachShipperDeliveryMode({
        id: shipper_id,
        delivery_mode_id: input.delivery_mode_id,
        charge: input.charge,
        category: input.category,
        currency: input.currency,
        time_zone,
        operating_status: input.operating_status,
        ...operating_time,
      })
    );
    dispatch(showShipper(shipper_id));
    closeModal();
  };

  return (
    <React.Fragment>
      <Button.Primary
        type={"button"}
        title={"Attach Delivery Mode"}
        onClick={openModal}
      />

      <Transition appear show={visibility || isOpen} as={React.Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <div className="min-h-screen px-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay
                className="fixed inset-0"
                style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
              />
            </Transition.Child>

            <div className="fixed z-10 inset-0 overflow-y-auto">
              <div className="flex items-center justify-center min-h-full p-4 sm:p-0">
                <Transition.Child
                  as={React.Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="relative bg-white dark:bg-[#121212] rounded-sm px-4 pt-5 pb-4 overflow-hidden shadow-xl transform transition-all sm:my-10 sm:max-w-[950px] sm:w-full sm:p-8">
                    <div>
                      <div className="sm:block absolute top-4 right-5">
                        <button
                          type="button"
                          className="bg-[#ffeae5] rounded-md p-1 sm:p-3"
                          onClick={closeModal}
                        >
                          <span className="sr-only">Close</span>
                          <img src={CLOSE} alt="close" className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="sm:flex">
                        <div className="text-left sm:text-left sm:px-8">
                          <Dialog.Title
                            as="p"
                            className="text-md font-medium text-[#717485] dark:text-white"
                          >
                            Attach Delivery Mode
                          </Dialog.Title>
                        </div>
                      </div>
                    </div>

                    <div className="border-b-2 mt-4"></div>
                    <div className="mt-5 sm:mx-auto w-full p-4">
                      <div className="w-full">
                        <div>
                          <Input.Label
                            title={"Delivery Mode"}
                            htmlFor={"delivery_mode_id"}
                            name={"delivery_mode_id"}
                            value={serviceArea}
                            placeholder={"Search delivery mode"}
                            onChange={handleSearch}
                          />
                          {submitted && !input.delivery_mode_id && (
                            <div className="text-primary text-xs">
                              Delivery mode is required
                            </div>
                          )}
                          {search &&
                            Array.isArray(search) &&
                            search.map((result, index) => (
                              <div
                                className="p-3 border border-primary mb-2 cursor-pointer"
                                id={result.id}
                                key={index}
                                onClick={() => selectDeliveryMode(result)}
                              >
                                <p className="text-sm font-medium">
                                  {result.title}
                                </p>
                              </div>
                            ))}
                        </div>

                        <div className="grid grid-cols-2 gap-2 sm:gap-4">
                          <div>
                            <Input.Select
                              title={"Category"}
                              htmlFor={"category"}
                              name={"category"}
                              onChange={handleChange}
                              defaultValue={input.category}
                            >
                              <option>Select a category</option>
                              <option value={"fragile"}>Fragile</option>
                              <option value={"non fragile"}>Non-Fragile</option>
                            </Input.Select>
                            {submitted && !input.category && (
                              <div className="text-primary text-xs">
                                Category is required
                              </div>
                            )}
                          </div>

                          <div>
                            <Input.Number
                              title={"Charge"}
                              htmlFor={"charge"}
                              name={"charge"}
                              placeholder={"E.g: 1000"}
                              defaultValue={input.charge}
                              onChange={handleChange}
                            />
                            {submitted && !input.charge && (
                              <div className="text-primary text-xs">
                                Base charge is required
                              </div>
                            )}
                          </div>

                          <div>
                            <Input.Select
                              title={"Currency"}
                              htmlFor={"currency"}
                              name={"currency"}
                              id={"currency"}
                              onChange={handleChange}
                            >
                              <option>Select Currency</option>
                              {currencies.map((currency, i) => (
                                <option key={i} value={currency.alphabetic_code}>
                                  {currency.alphabetic_code}
                                </option>
                              ))}
                            </Input.Select>
                            {submitted && !input.currency && (
                              <div className="text-primary text-xs">
                                Currency is required
                              </div>
                            )}
                          </div>

                          <div>
                            <Input.Select
                              title={"Timezone"}
                              name="time_zone"
                              value={time_zone}
                              onChange={(e) => setTimeZone(e.target.value)}
                            >
                              <option>Select Timezone</option>
                              {isLoading ? (
                                <option>Loading...</option>
                              ) : (
                                allTimezones &&
                                Object.keys(allTimezones).map((key, index) => (
                                  <option key={index} value={allTimezones[key].offset}>
                                    {allTimezones[key].common} ({allTimezones[key].offset})
                                  </option>
                                ))
                              )}
                            </Input.Select>
                            {submitted && !time_zone && (
                              <div className="text-primary text-xs">
                                Timezone is required
                              </div>
                            )}
                          </div>
                        </div>

                        <div>
                          <Input.Select
                            title={"Operating Status"}
                            name="operating_status"
                            onChange={handleChange}
                          >
                            <option>Select Status</option>
                            <option value="available">Available</option>
                            <option value="unavailable">Unavailable</option>
                          </Input.Select>
                          {submitted && !input.operating_status && (
                            <div className="text-primary text-xs">
                              Operating status is required
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-4">
                        <p className="text-sm font-medium text-[#151515] dark:text-white mb-3">
                          Operating Hours
                        </p>
                        <div className="grid grid-cols-2 gap-2 sm:gap-4">
                          {days.map((day) => (
                            <React.Fragment key={day}>
                              <Input.Label
                                title={`${day.charAt(0).toUpperCase() + day.slice(1)} (Opening Time)`}
                                type="time"
                                name={`operating_time_${day}_open`}
                                value={operating_time[`operating_time_${day}_open`]}
                                onChange={(e) => handleOperatingTimeChange(day, "open", e.target.value)}
                              />
                              <Input.Label
                                title={`${day.charAt(0).toUpperCase() + day.slice(1)} (Closing Time)`}
                                type="time"
                                name={`operating_time_${day}_close`}
                                value={operating_time[`operating_time_${day}_close`]}
                                onChange={(e) => handleOperatingTimeChange(day, "close", e.target.value)}
                              />
                            </React.Fragment>
                          ))}
                        </div>
                      </div>

                      <div className="mt-8 mb-3 text-center flex justify-center gap-5">
                        <Button.Primary
                          type={"button"}
                          disabled={updating}
                          title={updating ? <Beat /> : "Attach Delivery Mode"}
                          onClick={handleSubmit}
                        />
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition>
    </React.Fragment>
  );
});
